import { useMemo, useRef, useState, type FormEvent } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { newId } from '@gk/contracts'
import { useClub } from '../../club/ClubProvider.tsx'
import { asksReading, readersOf, useAcknowledgements, type AcknowledgementRow } from '../../data/acknowledgements.ts'
import { say, useDocuments, useDocumentVersions, useTemplateVersions, type DocumentRow, type DocumentVersionRow } from '../../data/plan.ts'
import { blankDoc, buildDocumentPublish, docProblems, draftOfDocVersion, fmtDay, langOf, MAX_BODY, procedureRows, stepsOf, type DocDraft, type ProcedureRow } from '../../data/planSetup.ts'
import { illustrationCount, illustrationProblems, illustrationsOf, keptOf, MAX_CAPTION, MAX_ILLUSTRATIONS, useIllustrationLinks, type IllustrationLink, type KeptIllustration } from '../../data/procedurePhotos.ts'
import type { PreparedPhoto } from '../../photos/capture.ts'
import { IllustrationImage, useBlobUrl } from '../../photos/Illustration.tsx'
import { illustrateFiles, illustrateOptions } from '../../photos/lane.ts'
import { PhotoQuotaError } from '../../photos/store.ts'
import { usePhotoDraft } from '../../photos/usePhotos.ts'
import { Banner, Field, Pill } from '../../ui/components.tsx'
import { useWorkshopAction } from '../../ui/hooks.ts'
import { CheckRow, Feedback, Problems, useSetupAccess } from './shared.tsx'

type Access = ReturnType<typeof useSetupAccess>
type Action = ReturnType<typeof useWorkshopAction>
const THUMB = 'size-16 shrink-0 rounded-lg border-2 border-slate-300 object-cover dark:border-slate-600'

/** A numbered list of the lines of a text: the way a job card will show the steps. */
function Steps({ text, testId }: { text: string; testId?: string }) {
  const steps = stepsOf(text)
  if (steps.length === 0) return null
  return <ol className="ml-6 list-decimal text-sm" data-testid={testId}>{steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
}

/** Procedures: documents of kind "procedure" (the mowing route, where the rakes go). A version is frozen; a change publishes the next one and the old one stays readable.
 *  M16-I1: a version carries up to six pictures with captions, attached in the same publish flow; the next version takes its own (the ones kept are re-linked).
 *  M16a finding 24: the pictures are the publisher's own command, `document.illustrate@1` (procedure.publish), never the crew's photo path.
 *  M16-I2: "demander une lecture" per procedure (off by default; `document.set_ask_reading@1`, the publisher's), and how many people read the version in force. */
export function ProceduresTab() {
  const { t, i18n } = useTranslation()
  const lang = langOf(i18n.language)
  const docs = useDocuments(); const versions = useDocumentVersions(); const tvs = useTemplateVersions(); const links = useIllustrationLinks(); const acks = useAcknowledgements()
  const access = useSetupAccess('procedure.publish')
  const w = useWorkshopAction()
  const [editing, setEditing] = useState<{ from: ProcedureRow | null } | null>(null)
  const [open, setOpen] = useState<string | null>(null)
  const rows = useMemo(() => procedureRows(docs ?? [], versions ?? [], tvs ?? [], lang), [docs, versions, tvs, lang])
  if (!docs || !versions || !tvs) return <p>{t('common.loading')}</p>

  if (editing) return <ProcedureForm key={editing.from?.doc.id ?? 'new'} from={editing.from} links={links ?? []} w={w} access={access} onClose={() => setEditing(null)} />
  return (
    <section className="flex flex-col gap-4" data-testid="doc-list-view">
      <div className="flex flex-col items-start gap-3">
        <div className="min-w-0">
          <h2 className="text-xl font-bold">{t('planSetup.docs.title')}</h2>
          <p className="text-sm opacity-75">{t('planSetup.docs.help')}</p>
          {/* M16-I2: what asking for a reading does, said once over the list */}
          <p className="meta mt-1" data-testid="doc-ask-help">{t('planSetup.docs.askHelp')}</p>
        </div>
        {access.permitted && <button className="btn btn-primary !min-h-11" disabled={!access.canSave} onClick={() => { w.reset(); setEditing({ from: null }) }} data-testid="doc-new">{t('planSetup.docs.new')}</button>}
      </div>
      {!access.permitted && <Banner tone="info">{t('planSetup.notAllowed')}</Banner>}
      <Feedback error={w.error} ok={w.ok} pending={w.pending} />
      {rows.length === 0 && <p className="opacity-70" data-testid="doc-none">{t('planSetup.docs.none')}</p>}
      <ul className="flex flex-col gap-3" data-testid="doc-list">
        {rows.map((r) => {
          const photos = illustrationsOf(links ?? [], r.latest.id).length
          return (
            <li key={r.doc.id} className="card flex flex-col gap-2" data-testid="doc-row" data-document-id={r.doc.id}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <p className="text-lg font-bold" data-testid="doc-row-title">{say(r.latest.title, lang)}</p>
                <Pill tone="green">{t('planSetup.docs.version', { n: r.latest.version_no })}</Pill>
              </div>
              <p className="text-sm opacity-80">{t('planSetup.docs.effective', { date: fmtDay(r.latest.effective_from) })}</p>
              <p className="text-sm opacity-80" data-testid="doc-template-count">{t('planSetup.docs.templates', { count: r.templateVersions })}</p>
              {photos > 0 && <p className="meta" data-testid="doc-photo-count" data-count={photos}>{t('planSetup.docs.photoCount', { count: photos })}</p>}
              {r.outdatedUsers > 0 && <div><Pill tone="amber">{t('planSetup.docs.outdated', { count: r.outdatedUsers })}</Pill></div>}
              <AskReading doc={r.doc} latest={r.latest} acks={acks ?? []} w={w} access={access} />
              <div className="flex flex-col gap-2 sm:flex-row">
                {access.permitted && <button className="btn !min-h-11" disabled={!access.canSave} onClick={() => { w.reset(); setEditing({ from: r }) }} data-testid="doc-new-version">{t('planSetup.newVersion')}</button>}
                <button className="btn !min-h-11" aria-expanded={open === r.doc.id} onClick={() => setOpen(open === r.doc.id ? null : r.doc.id)} data-testid="doc-history">{open === r.doc.id ? t('planSetup.docs.historyHide') : t('planSetup.docs.historyShow', { n: r.history.length })}</button>
              </div>
              {open === r.doc.id && <History versions={r.history} links={links ?? []} lang={lang} />}
            </li>
          )
        })}
      </ul>
    </section>
  )
}

/** M16-I2: "Demander une lecture", a 56-px switch for the publisher (the fonctions' switch pattern: the state in words, held while a tap is on its way, the
 *  server's verdict in the list's feedback); the others read whether it asks. While it asks, how many people read the version in force — a count, never a
 *  list here (the list per person is Qualifications', the direction's). */
function AskReading({ doc, latest, acks, w, access }: { doc: DocumentRow; latest: DocumentVersionRow; acks: readonly AcknowledgementRow[]; w: Action; access: Access }) {
  const { t } = useTranslation()
  const { can } = useClub()
  const asks = asksReading(doc)
  const readers = readersOf(acks, latest.id)
  return (
    <div className="flex flex-col gap-1" data-testid="doc-ask" data-asks={asks ? '1' : '0'}>
      {access.permitted
        ? <button type="button" role="switch" aria-checked={asks} className="btn w-full justify-between" disabled={!access.canSave || w.busy || w.pending}
            onClick={() => void w.run('document.set_ask_reading@1', { document_id: doc.id, ask_reading: !asks }, asks ? 'planSetup.docs.askOff' : 'planSetup.docs.askOn', { aggregateId: doc.id })} data-testid="doc-ask-reading">
            <span>{t('planSetup.docs.askReading')}</span><Pill tone={asks ? 'green' : 'gray'}>{asks ? t('common.yes') : t('common.no')}</Pill>
          </button>
        : asks && <p className="flex items-center gap-2 text-sm">{t('planSetup.docs.askReading')}<Pill tone="green">{t('common.yes')}</Pill></p>}
      {/* release b review (finding 14): the count leads to who read and who has not (Réglages › Employés › Qualifications' readings, reached in one tap) */}
      {asks && access.permitted && (can('qualification.read')
        ? <Link to="/qualifications" hash="readings" className="btn w-full justify-between" data-testid="doc-readers" data-count={readers}>
            <span>{t('planSetup.docs.readers', { count: readers, n: latest.version_no })}</span><span aria-hidden="true">›</span>
          </Link>
        : <p className="meta" data-testid="doc-readers" data-count={readers}>{t('planSetup.docs.readers', { count: readers, n: latest.version_no })}</p>)}
    </div>
  )
}

/** Every version, newest first, read only: an older wording is what an old job was done with. */
function History({ versions, links, lang }: { versions: DocumentVersionRow[]; links: readonly IllustrationLink[]; lang: 'fr' | 'en' }) {
  const { t } = useTranslation()
  return (
    <ul className="flex flex-col gap-2" data-testid="doc-versions">
      {versions.map((v, i) => {
        const photos = illustrationsOf(links, v.id).length
        return (
          <li key={v.id} className="rounded-xl border-2 border-slate-300 p-3 dark:border-slate-600" data-testid="doc-version-row" data-version-no={v.version_no}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-semibold">{t('planSetup.docs.version', { n: v.version_no })} · {say(v.title, lang)}</p>
              <Pill tone={i === 0 ? 'green' : 'gray'}>{i === 0 ? t('planSetup.docs.current') : t('planSetup.docs.replaced')}</Pill>
            </div>
            <p className="meta">{t('planSetup.docs.effective', { date: fmtDay(v.effective_from) })}{photos > 0 && <> · <span data-testid="doc-version-photos">{t('planSetup.docs.photoCount', { count: photos })}</span></>}</p>
            <details className="mt-1">
              <summary className="min-h-11 cursor-pointer py-2 text-sm font-semibold">{t('planSetup.docs.showText')}</summary>
              <div className="flex flex-col gap-2">
                {v.body?.fr && <div><p className="meta font-semibold">{t('planSetup.docs.textFr')}</p><Steps text={v.body.fr} /></div>}
                {v.body?.en && <div><p className="meta font-semibold">{t('planSetup.docs.textEn')}</p><Steps text={v.body.en} /></div>}
              </div>
            </details>
          </li>
        )
      })}
    </ul>
  )
}

/** `from` null = a new procedure; otherwise the form starts from the version in force and publishes the next one of the same document. The pictures follow the version:
 *  once it is published (online: the server's verdict), ONE `document.illustrate@1` names the kept ones (re-linked: the same stored file) and the new ones (the bytes
 *  leave the device with the command, in one local transaction, and the lane uploads them once it is applied), answered at once like the publish; refused, the
 *  form stays open with the reason and the version published, and the next tap sends the same pictures again (the server adds nothing twice). */
function ProcedureForm({ from, links, w, access, onClose }: { from: ProcedureRow | null; links: readonly IllustrationLink[]; w: Action; access: Access; onClose: () => void }) {
  const { t, i18n } = useTranslation()
  const lang = langOf(i18n.language)
  const { db, org, site } = useClub()
  const ids = useRef({ documentId: from?.doc.id ?? newId(), versionId: newId() })
  const [d, setD] = useState<DocDraft>(() => (from ? draftOfDocVersion(from.latest) : blankDoc()))
  const [kept, setKept] = useState<KeptIllustration[]>(() => (from ? keptOf(links, from.latest.id) : []))
  const draft = usePhotoDraft()
  const [captions, setCaptions] = useState<Record<string, string>>({})
  const [published, setPublished] = useState(false)
  const [photoError, setPhotoError] = useState<string | null>(null)
  const fresh = draft.photos.map((p) => ({ sha256: p.sha256, caption: captions[p.sha256] ?? '' }))
  const problems = [...docProblems(d), ...illustrationProblems(kept, fresh)]
  const count = illustrationCount(kept, fresh.length)
  const full = count >= MAX_ILLUSTRATIONS
  const input = useRef<HTMLInputElement>(null)
  const setKeptAt = (i: number, patch: Partial<KeptIllustration>) => setKept((all) => all.map((k, j) => (j === i ? { ...k, ...patch } : k)))

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (problems.length > 0) return
    if (!published) {
      if (!(await w.run('document.publish_version@1', buildDocumentPublish(ids.current, d, from === null), 'planSetup.docs.published', { aggregateId: ids.current.documentId }))) return
      setPublished(true)
    }
    // M16a finding 24: the pictures are the publisher's own command (procedure.publish), answered at once; nothing to send when the version has none
    const files = illustrateFiles(kept, draft.photos.map((photo) => ({ photo, caption: captions[photo.sha256] ?? '' })))
    if (files.length > 0) {
      setPhotoError(null)
      let opts
      try { opts = await illustrateOptions(db, org.org_id, site.site_id, draft.photos) } catch (err) {
        setPhotoError(t('planSetup.docs.photosFailed', { error: err instanceof PhotoQuotaError ? t('photos.quota') : err instanceof Error && err.message ? err.message : t('errors.unknown') }))
        return
      }
      if (!(await w.run('document.illustrate@1', { document_version_id: ids.current.versionId, files }, 'planSetup.docs.published', { ...opts, aggregateId: ids.current.documentId }))) return
    }
    onClose()
  }
  return (
    <form noValidate onSubmit={(e) => void submit(e)} className="flex flex-col gap-4" data-testid="doc-form">
      <button type="button" className="-mx-2 inline-flex min-h-14 w-full items-center gap-2 px-2 text-left text-base font-semibold" onClick={onClose} data-testid="doc-cancel">← {t('common.back')}</button>
      <h2 className="text-xl font-bold">{from ? t('planSetup.docs.versionTitle', { title: say(from.latest.title, lang) }) : t('planSetup.docs.newTitle')}</h2>
      {from && <Banner tone="info">{t('planSetup.docs.versionHelp', { n: from.latest.version_no, next: from.latest.version_no + 1 })}</Banner>}

      <Field label={t('planSetup.docs.titleFr')} hint={t('planSetup.docs.titleHelp')} htmlFor="doc-title-fr"><input id="doc-title-fr" className="input" maxLength={120} value={d.titleFr} onChange={(e) => setD({ ...d, titleFr: e.target.value })} data-testid="doc-title-fr" disabled={published} /></Field>
      <Field label={t('planSetup.docs.titleEn')} htmlFor="doc-title-en"><input id="doc-title-en" className="input" maxLength={120} value={d.titleEn} onChange={(e) => setD({ ...d, titleEn: e.target.value })} data-testid="doc-title-en" disabled={published} /></Field>
      <Field label={t('planSetup.docs.bodyFr')} hint={t('planSetup.docs.bodyHelp')} htmlFor="doc-body-fr">
        <textarea id="doc-body-fr" className="input min-h-40" maxLength={MAX_BODY} value={d.bodyFr} onChange={(e) => setD({ ...d, bodyFr: e.target.value })} data-testid="doc-body-fr" disabled={published} />
      </Field>
      {stepsOf(d.bodyFr).length > 0 && <div><p className="meta font-semibold">{t('planSetup.docs.preview')}</p><Steps text={d.bodyFr} testId="doc-preview-fr" /></div>}
      <Field label={t('planSetup.docs.bodyEn')} htmlFor="doc-body-en">
        <textarea id="doc-body-en" className="input min-h-40" maxLength={MAX_BODY} value={d.bodyEn} onChange={(e) => setD({ ...d, bodyEn: e.target.value })} data-testid="doc-body-en" disabled={published} />
      </Field>
      {stepsOf(d.bodyEn).length > 0 && <div><p className="meta font-semibold">{t('planSetup.docs.preview')}</p><Steps text={d.bodyEn} testId="doc-preview-en" /></div>}

      <fieldset className="flex flex-col gap-3" data-testid="doc-photos" data-count={count}>
        <legend className="text-sm font-semibold">{t('planSetup.docs.photosTitle')}</legend>
        <p className="meta">{t('planSetup.docs.photosHelp', { max: MAX_ILLUSTRATIONS })}</p>
        {kept.length > 0 && (
          <div className="flex flex-col gap-2" data-testid="doc-photos-kept">
            <p className="meta">{t('planSetup.docs.photoKeepHelp', { n: from?.latest.version_no ?? 0 })}</p>
            <ul className="flex flex-col gap-2">
              {kept.map((k, i) => (
                <li key={k.link.id} className="flex flex-col gap-2 rounded-xl border-2 border-slate-300 p-2 dark:border-slate-600" data-testid="doc-photo-kept" data-keep={k.keep ? 'yes' : 'no'} data-sha={k.link.sha256}>
                  <div className="flex items-center gap-3">
                    {/* the version in force was published online and its pictures uploaded by the lane since: storage (or the cache) has them */}
                    <IllustrationImage link={k.link} alt={k.link.caption ?? t('photos.alt')} className={THUMB} testId="doc-photo-kept-thumb" />
                    <div className="min-w-0 flex-1">
                      <CheckRow checked={k.keep} onChange={(v) => setKeptAt(i, { keep: v })} testId={`doc-photo-keep-${i}`} disabled={published}>{t('planSetup.docs.photoKeep', { n: i + 1 })}</CheckRow>
                    </div>
                  </div>
                  <Field label={t('planSetup.docs.photoCaption', { n: i + 1 })} htmlFor={`doc-photo-kept-caption-${i}`}>
                    <input id={`doc-photo-kept-caption-${i}`} className="input" maxLength={MAX_CAPTION} value={k.caption} disabled={!k.keep || published} onChange={(e) => setKeptAt(i, { caption: e.target.value })} data-testid={`doc-photo-kept-caption-${i}`} />
                  </Field>
                </li>
              ))}
            </ul>
          </div>
        )}
        {draft.photos.length > 0 && (
          <ul className="flex flex-col gap-2" data-testid="doc-photos-new">
            {draft.photos.map((p, i) => (
              <li key={p.sha256} className="flex flex-col gap-2 rounded-xl border-2 border-slate-300 p-2 dark:border-slate-600" data-testid="doc-photo-new" data-sha={p.sha256}>
                <div className="flex items-center gap-3">
                  <NewThumb photo={p} />
                  <p className="min-w-0 flex-1 font-medium">{t('planSetup.docs.photoNew', { n: kept.length + i + 1 })}</p>
                  <button type="button" className="btn !min-h-11" onClick={() => { draft.remove(p.sha256); setCaptions((c) => { const { [p.sha256]: _gone, ...rest } = c; return rest }) }} disabled={published} data-testid={`doc-photo-remove-${i}`}>{t('planSetup.docs.photoRemove')}</button>
                </div>
                <Field label={t('planSetup.docs.photoCaption', { n: kept.length + i + 1 })} htmlFor={`doc-photo-caption-${i}`}>
                  <input id={`doc-photo-caption-${i}`} className="input" maxLength={MAX_CAPTION} value={captions[p.sha256] ?? ''} disabled={published} onChange={(e) => setCaptions((c) => ({ ...c, [p.sha256]: e.target.value }))} data-testid={`doc-photo-caption-${i}`} />
                </Field>
              </li>
            ))}
          </ul>
        )}
        {/* the pictures go with the publisher's own command (procedure.publish): no other permission is asked of the person who publishes */}
        {access.permitted && (
          <>
            <button type="button" className="btn" disabled={draft.busy || full || published || !access.allowed} onClick={() => input.current?.click()} data-testid="doc-photo-add">{draft.busy ? t('photos.preparing') : t('planSetup.docs.photoAdd')}</button>
            {/* the library as well as the camera: a pattern is as often a drawing as a shot */}
            <input ref={input} type="file" accept="image/*" className="sr-only" tabIndex={-1} aria-hidden data-testid="doc-photo-input" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void draft.addFile(f) }} />
            {full && <p className="meta" data-testid="doc-photo-full">{t('planSetup.docs.photoMax', { max: MAX_ILLUSTRATIONS })}</p>}
            {draft.error && <Banner tone="warn"><span data-testid="doc-photo-error">{draft.error}</span></Banner>}
          </>
        )}
      </fieldset>

      <Problems codes={problems} />
      <Feedback error={w.error} pending={w.pending} />
      {photoError && <Banner tone="warn"><span data-testid="doc-photos-failed">{photoError}</span></Banner>}
      <button className="btn btn-primary" disabled={w.busy || draft.busy || !access.canSave || problems.length > 0} data-testid="doc-save">{w.busy ? t('common.saving') : published ? t('planSetup.docs.savePhotos') : t('planSetup.docs.publish')}</button>
      {!access.online && <p className="meta">{t('planSetup.needsNetwork')}</p>}
    </form>
  )
}

function NewThumb({ photo }: { photo: PreparedPhoto }) {
  const { t } = useTranslation()
  const url = useBlobUrl(photo.blob)
  return url ? <img src={url} alt={t('photos.alt')} className={THUMB} data-testid="doc-photo-new-thumb" /> : <span className={THUMB} />
}
